import { programPassportData, programResidenceData } from "./programData";
import { ImmigrationProgram } from "../types/program";

export type ProgramCategory = "residence" | "passport";

export type FlatProgram = {
  slug: string;
  category: ProgramCategory;
  data: ImmigrationProgram;
};

export function getProgram(category: ProgramCategory, slug: string): ImmigrationProgram {
  const source = category === "residence" ? programResidenceData : programPassportData;
  return (source as Record<string, ImmigrationProgram>)[slug];
}

export function getAllPrograms(): FlatProgram[] {
  const residence = Object.entries(programResidenceData).map(
    ([slug, data]) => ({ slug, category: "residence" as const, data }),
  );
  const passport = Object.entries(programPassportData).map(
    ([slug, data]) => ({ slug, category: "passport" as const, data }),
  );
  return [...residence, ...passport];
}

// `FlatProgram.slug` (and `MatchResult.slug`) is this file's internal lookup
// key into programResidenceData/programPassportData (via getProgram()) — it
// must stay the legacy key or hydrateMatches() breaks. The live CMS routes
// renamed Malta's two programs to "malta-residence"/"malta-passport" (needed
// distinct slugs in the CMS's flat namespace, unlike this file's separate
// residence/passport keyed maps where both could just be "malta"), so any
// caller building a /program/:type/:slug link from one of these slugs needs
// this instead of the raw slug.
export function toRoutableSlug(slug: string, category: ProgramCategory): string {
  if (slug === "malta") return category === "residence" ? "malta-residence" : "malta-passport";
  return slug;
}

// Approximate CNY conversion rates, used only to rank programs by relative
// budget fit -- not financial advice, not shown to users as an exchange rate.
const CNY_PER_UNIT: Record<string, number> = {
  美元: 7.25,
  美金: 7.25,
  欧元: 7.85,
  日圆: 0.048,
  澳元: 4.75,
  英镑: 9.1,
  迪拉姆: 1.97,
  新元: 5.35,
};

export function investmentInCny(program: ImmigrationProgram): number {
  const amount = program.investmentAmount.set || program.investmentAmount.optional || 0;
  if (!amount) return 0;
  const rate = CNY_PER_UNIT[program.currency.title] ?? 1;
  return amount * rate;
}

function durationInMonths(program: ImmigrationProgram): number {
  const { max, unit } = program.process.duration;
  return unit === "年" ? max * 12 : max;
}

// A handful of programs' duration is a single value, not a genuine range
// (e.g. Georgia/Nauru's own data has max stuck at 0 rather than mirroring
// min) — showing "6-0个月" is worse than showing nothing extra, so this
// collapses to just the one number whenever max isn't a real upper bound.
export function formatDuration({ min, max, unit }: { min: number; max: number; unit: string }): string {
  if (!max || max === min) return `${min}${unit}`;
  return `${min}-${max}${unit}`;
}

export type BudgetKey = "under50" | "50to150" | "150to500" | "above500";
export type GoalKey = "residence" | "passport" | "either";
export type RegionKey =
  | "europe"
  | "americas"
  | "middleEast"
  | "asiaPacific"
  | "africa"
  | "any";
export type TimelineKey = "fast" | "normal" | "flexible";

export type AssessmentAnswers = {
  budget: BudgetKey;
  goal: GoalKey;
  region: RegionKey;
  timeline: TimelineKey;
};

export const BUDGET_OPTIONS: { key: BudgetKey; label: string; hint: string }[] = [
  { key: "under50", label: "100万—500万元", hint: "灵活预算，注重高性价比" },
  { key: "50to150", label: "500万—1000万元", hint: "欧洲主流身份项目区间" },
  { key: "150to500", label: "1000万—3000万元", hint: "覆盖多数护照与优质居留项目" },
  { key: "above500", label: "3000万以上", hint: "可考虑高端定制方案" },
];

export const GOAL_OPTIONS: { key: GoalKey; label: string; hint: string }[] = [
  { key: "residence", label: "居留身份", hint: "长期生活、工作、子女教育" },
  { key: "passport", label: "海外永居或者护照", hint: "国际通行、资产配置便利" },
  { key: "either", label: "两者都可以", hint: "先看看有哪些合适的选择" },
];

export const REGION_OPTIONS: { key: RegionKey; label: string }[] = [
  { key: "europe", label: "欧洲" },
  { key: "americas", label: "美洲 · 加勒比" },
  { key: "middleEast", label: "中东" },
  { key: "asiaPacific", label: "亚太" },
  { key: "africa", label: "非洲" },
  { key: "any", label: "不确定，都可以看看" },
];

export const TIMELINE_OPTIONS: { key: TimelineKey; label: string; hint: string }[] = [
  { key: "fast", label: "6个月以内", hint: "希望尽快拿到身份" },
  { key: "normal", label: "6-12个月", hint: "正常规划节奏" },
  { key: "flexible", label: "12个月以上", hint: "时间充裕，重点看方案质量" },
];

const BUDGET_RANGES_CNY: Record<BudgetKey, [number, number]> = {
  under50: [1000000, 5000000],
  "50to150": [5000000, 10000000],
  "150to500": [10000000, 30000000],
  above500: [30000000, Infinity],
};

const CONTINENT_TO_REGION: Record<string, RegionKey> = {
  欧洲: "europe",
  美洲: "americas",
  中美洲: "americas",
  北美洲: "americas",
  中东: "middleEast",
  亚洲: "asiaPacific",
  大洋洲: "asiaPacific",
  非洲: "africa",
};

const TIMELINE_MAX_MONTHS: Record<TimelineKey, number> = {
  fast: 6,
  normal: 12,
  flexible: 9999,
};

function budgetScore(program: ImmigrationProgram, answers: AssessmentAnswers) {
  const cny = investmentInCny(program);
  if (cny === 0) return 92;
  const [lo, hi] = BUDGET_RANGES_CNY[answers.budget];
  if (cny >= lo && cny <= hi) return 100;
  if (cny < lo) return 78;
  const overRatio = cny / hi;
  return Math.max(10, Math.round(100 - (overRatio - 1) * 60));
}

function goalScore(category: ProgramCategory, answers: AssessmentAnswers) {
  if (answers.goal === "either") return 100;
  return category === answers.goal ? 100 : 25;
}

function regionScore(program: ImmigrationProgram, answers: AssessmentAnswers) {
  if (answers.region === "any") return 100;
  const bucket = CONTINENT_TO_REGION[program.continent];
  return bucket === answers.region ? 100 : 40;
}

function timelineScore(program: ImmigrationProgram, answers: AssessmentAnswers) {
  const maxAllowed = TIMELINE_MAX_MONTHS[answers.timeline];
  const programMonths = durationInMonths(program);
  if (programMonths <= maxAllowed) return 100;
  const overRatio = programMonths / maxAllowed;
  return Math.max(15, Math.round(100 - (overRatio - 1) * 50));
}

export type MatchResult = FlatProgram & {
  score: number;
  reasons: string[];
};

function buildReasons(
  program: ImmigrationProgram,
  category: ProgramCategory,
  answers: AssessmentAnswers,
  scores: { budget: number; goal: number; region: number; timeline: number },
): string[] {
  const reasons: string[] = [];
  if (scores.goal >= 100) {
    reasons.push(category === "passport" ? "符合您对第二护照的规划目标" : "符合您对居留身份的规划目标");
  }
  if (scores.budget >= 90) {
    reasons.push("投资门槛与您的预算区间吻合");
  }
  if (scores.region >= 100 && answers.region !== "any") {
    const label = REGION_OPTIONS.find((r) => r.key === answers.region)?.label;
    reasons.push(`地区符合您的意向（${label}）`);
  }
  if (scores.timeline >= 90) {
    const { min, max, unit } = program.process.duration;
    reasons.push(`办理周期约${min}-${max}${unit}，符合您的时效需求`);
  }
  if (reasons.length === 0) {
    reasons.push("综合各项条件后仍具备一定参考价值");
  }
  return reasons.slice(0, 3);
}

// MatchResult embeds the full ImmigrationProgram, whose `requirements` fields
// hold React elements -- not serializable, so it can't be passed through
// react-router navigate state (history.pushState requires structured-clone-able
// data). Strip it down to plain data for navigation, then hydrate it back on
// the receiving page via getProgram().
export type SerializableMatch = {
  slug: string;
  category: ProgramCategory;
  score: number;
  reasons: string[];
};

export function toSerializableMatches(matches: MatchResult[]): SerializableMatch[] {
  return matches.map(({ slug, category, score, reasons }) => ({
    slug,
    category,
    score,
    reasons,
  }));
}

export function hydrateMatches(serialized: SerializableMatch[]): MatchResult[] {
  return serialized.map((m) => ({
    ...m,
    data: getProgram(m.category, m.slug),
  }));
}

export function matchPrograms(answers: AssessmentAnswers, limit = 5): MatchResult[] {
  const all = getAllPrograms();
  const scored = all.map((program) => {
    const scores = {
      budget: budgetScore(program.data, answers),
      goal: goalScore(program.category, answers),
      region: regionScore(program.data, answers),
      timeline: timelineScore(program.data, answers),
    };
    const score = Math.round(
      scores.budget * 0.35 + scores.goal * 0.3 + scores.region * 0.2 + scores.timeline * 0.15,
    );
    return {
      ...program,
      score,
      reasons: buildReasons(program.data, program.category, answers, scores),
    };
  });
  return scored.sort((a, b) => b.score - a.score).slice(0, limit);
}
